import { PlusOutlined, RightOutlined } from "@ant-design/icons";
import {
  InputRef,
  Button,
  Card,
  TableProps,
  Flex,
  Table,
} from "antd";
import PageHeading from "components/PageHeading.component";
import StatBand from "components/StatBand.component";
import FileUpload from "components/buttons/CsvUpload.component";
import NewClassModal from "components/modals/NewClassModal.component";
import { getAllClassTypeByBusinessId } from "hooks/queries/classtype";
import { useOpenCreateFromNav } from "hooks/useOpenCreateFromNav";
import { ClassTypeBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { useState, useRef, CSSProperties } from "react";
import { FilterValue } from "antd/lib/table/interface";
import { useNavigate } from "react-router-dom";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { PAGE_SIZE } from "shared/constants";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import { colors } from "shared/theme/color";
import { ClassType } from "shared/types";
import { ClassCategory } from "shared/types/enum";
import { formatClassDuration } from "./ClassFormFields";
import { textPx } from "shared/constants/zoom";

// Minimalist uppercase ink-3 column header (prototype `table.tbl th`).
const thStyle: CSSProperties = {
  textTransform: "uppercase",
  fontSize: textPx(11),
  fontWeight: 600,
  letterSpacing: "0.06em",
  color: colors.ink3,
};
const th = (label: string) => <span style={thStyle}>{label}</span>;
const tnum: CSSProperties = { fontVariantNumeric: "tabular-nums" };

// The band's segments mirror the Tipe column's filter values; "all" means no
// filter (every class type visible), which is also this page's default.
type TypeSegment = "all" | "open" | "private";

const ClassPage = () => {
  const navigate = useNavigate();
  const { data: classes, isLoading } = getAllClassTypeByBusinessId();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { page, handleTableChange } = useRememberedTablePage("classes");

  // ClassCategory is exactly open | private, so the two slices always sum to
  // the total and match the Tipe filter's predicate.
  const totalCount = classes?.length ?? 0;
  const openCount =
    classes?.filter((cls) => cls.type === ClassCategory.Open).length ?? 0;
  const privateCount = totalCount - openCount;

  // Single source of truth for the Tipe column's filteredValue; the band
  // highlight and the table both read it. Unlike the people lists there is
  // no hidden-by-default slice, so fresh visits show everything.
  const [typeFilter, setTypeFilter] = useState<string[] | null>(null);
  const activeSegment: TypeSegment =
    typeFilter?.length === 1 ? (typeFilter[0] as TypeSegment) : "all";

  const selectSegment = (key: TypeSegment) => {
    // Re-tapping the selected slice widens back to every class.
    const next = key === "all" || key === activeSegment ? null : [key];
    setTypeFilter(next);
    // AntD auto-resets pagination only on dropdown-driven filter changes; a
    // controlled filteredValue change must reset the page itself.
    handleTableChange({ current: 1 });
  };

  const searchInput = useRef<InputRef>(null);

  // AntD runs a table's filters as either all-controlled or all-uncontrolled.
  // The band-driven column above is controlled, which flips the whole table
  // into controlled mode, so the search columns must be controlled as well or
  // AntD silently drops whatever the user types into them.
  const [searchFilters, setSearchFilters] = useState<
    Record<string, FilterValue | null>
  >({});

  // Pembatasan Admin: creating a class is a catalogue write. The nav-state
  // shortcut is guarded too, or a stale `openCreate` could open the modal.
  const { isRestricted } = useAdminCapabilities();
  const catalogRestricted = isRestricted(AdminCapability.CatalogPricing);
  // Dashboard hero quick action: land with the create modal already open.
  useOpenCreateFromNav(() => {
    if (!catalogRestricted) setIsModalOpen(true);
  });

  const columns: TableProps<ClassType>["columns"] = [
    {
      title: th(translate("label.class_name")),
      dataIndex: "name",
      key: "name",
      render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
      ...getColumnSearchProps({ config: "name", searchInput }),
      filteredValue: searchFilters.name ?? null,
    },
    {
      title: th(translate("label.class_type")),
      dataIndex: "type",
      key: "type",
      render: (type: ClassCategory) => <ClassTypeBadge type={type} />,
      onFilter: (value, record) =>
        record.type
          .toString()
          .toLowerCase()
          .includes((value as string).toLowerCase()),
      filters: [
        { value: "private", text: "Private" },
        { value: "open", text: "Open" },
      ],
      // Controlled by the stat band above the table (single source of truth
      // for both the band highlight and this dropdown).
      filteredValue: typeFilter,
    },
    {
      title: th(translate("label.max_participants")),
      dataIndex: "max_participants",
      key: "max_participants",
      render: (value: number) => <span style={tnum}>{value}</span>,
      sorter: (a, b) => a.max_participants - b.max_participants,
    },
    {
      title: th(translate("label.credits")),
      dataIndex: "credit_cost",
      key: "credit_cost",
      render: (value?: number) => <span style={tnum}>{value ?? 1}</span>,
      sorter: (a, b) => (a.credit_cost ?? 1) - (b.credit_cost ?? 1),
    },
    {
      title: th(translate("class_form.duration")),
      dataIndex: "duration",
      key: "duration",
      render: (duration: number) => (
        <span style={tnum}>{formatClassDuration(duration)}</span>
      ),
      sorter: (a, b) => a.duration - b.duration,
    },
    {
      key: "chevron",
      width: 40,
      align: "right",
      render: () => <RightOutlined style={{ color: colors.ink3, fontSize: textPx(12) }} />,
    },
  ];

  return (
    <>
      <NewClassModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(!isModalOpen)}
      />
      <PageHeading title={translate("class")} />
      <Flex vertical gap={20}>
        {/* Timeless list facts only: each segment filters the table to its
            class type (re-tap to widen back to every class). Open/Private
            stay English on every surface (ClassTypeBadge, the Tipe filter),
            so the band matches. */}
        <StatBand
          segments={[
            {
              key: "all",
              value: totalCount,
              label: translate("list_header.total_classes"),
            },
            { key: "open", value: openCount, label: "Open" },
            { key: "private", value: privateCount, label: "Private" },
          ]}
          selectedKey={activeSegment}
          onSelect={(key) => selectSegment(key as TypeSegment)}
        />

        <Card styles={{ body: { padding: 18 } }}>
          {/* Toolbar: Unggah CSV (secondary) + Tambah Kelas (navy
              primary), grouped right-aligned above the table. */}
          <Flex justify="flex-end" gap={10} style={{ marginBottom: 14 }}>
            <FileUpload
              endpoint={"class"}
              templateUrl="/template-import-kelas.csv"
            />
            <Button
              onClick={() => setIsModalOpen(!isModalOpen)}
              type="primary"
              icon={<PlusOutlined />}
              disabled={catalogRestricted}
            >
              {translate("button.add")} {translate("class")}
            </Button>
          </Flex>
          <Table
            showSorterTooltip={false}
            sticky
            rowKey="id"
            size={"middle"}
            loading={isLoading}
            columns={columns}
            dataSource={classes}
            onRow={(record) => {
              return {
                onClick: () => navigate(`${PATHS.CLASS}/${record.id}`),
                style: { cursor: "pointer" },
              };
            }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
            }}
            onChange={(pagination, filters) => {
              handleTableChange(pagination);
              // Keep the band highlight in sync when the filter changes via
              // the column dropdown (fires on page/sort too; re-setting the
              // same value is a no-op).
              setSearchFilters(filters);
              setTypeFilter((filters.type as string[] | null) ?? null);
            }}
          />
        </Card>
      </Flex>
    </>
  );
};

export default ClassPage;
